import { Accessor } from 'solid-js';

export enum ContentType {
  PointCloud = 'PointCloud',
  TrimBim = 'TrimBim',
  Panorama = 'Panorama',
  Planar = 'Planar',
  Map = 'Map',
}

export interface PaneContentBase {
  type: ContentType;
}

export interface PointCloudPaneContent extends PaneContentBase {
  type: ContentType.PointCloud;
  selectedProjectId: string;
  selectedPointCloudId: string;
}

export interface TrimBimPaneContent extends PaneContentBase {
  alignmentId: string;
  tenantTrn: string;
  type: ContentType.TrimBim;
}

export interface PanoramaPaneContent extends PaneContentBase {
  type: ContentType.Panorama;
  selectedProjectId: string;
  selectedPointCloudId: string;
}

/**
 * Placeholder mission TRN for point clouds that are visible in the project but not tied to
 * any mobile-mapping mission run (scan-data / SDS-only station loading).
 */
export const SYNTHETIC_MISSION_TRN_SCAN_DATA_ONLY_POINT_CLOUD =
  'trn:synthetic:numark:scan-data-only-point-cloud:mission';

/** Stable synthetic run id for SDS-only rows; encodes the point cloud TRN. */
export function syntheticRunIdForScanDataOnlyPointCloud(
  pointCloudTrn: string
): string {
  return `scan-data-only:${pointCloudTrn}`;
}

export interface PlanarPaneContent extends PaneContentBase {
  type: ContentType.Planar;
  selectedTenantTrn: string;
  selectedProjectId: string;
  selectedMissionTrn: string;
  selectedRunId: string;
  /** TRN of the point cloud used for SDS APIs (e.g. list stations). */
  selectedPointCloudTrn: string;
  /**
   * When true, this row is not backed by a Connect mission run. Do not call mobile-mapping
   * run APIs (`listCamerasQuery`); imaging is skipped and SDS supplement may still load.
   */
  scanDataOnly?: boolean;
}

export interface Map2DPaneContent extends PaneContentBase {
  type: ContentType.Map;
}

/**
 * Aggregate type representing content sources available across panes
 * in the mobile-mapping application.
 */
export type PaneContents = {
  alignmentContents: Accessor<TrimBimPaneContent[]>;
  pointCloudContents: Accessor<PointCloudPaneContent[]>;
  planarContents: Accessor<PlanarPaneContent[]>;
  map2DPaneContents: Accessor<Map2DPaneContent[]>;
};
